<template>
    <div>

        <Row>
            <Card crud :title="entity" @add="addEntity" :search="entitySearchQuery"
                @search="$event => entitySearchQuery = $event" :disabled="currentAction == 'add'">
                <DataTable crud :show-expand="expandable" v-if="!crudController[`${entity}Loading`]"
                    :items="crudController.data[entity]" :headers="entity" @edit="modifyEntity($event)"
                    @delete="crudController.deleteItem(endpoint, $event.id)" :loading="crudController.loading[entity]"
                    :search="entitySearchQuery">
                    <template #expanded-item="{ item }">
                        <slot name="expanded" :item="item"></slot>
                    </template>
                </DataTable>
            </Card>
        </Row>

        <Dialog :subject="entity" v-model="showEntityDialog" @cancel="cancelEntityAction" @save="saveEntityAction"
            @open="parent && filterEndpoint(entityData.id)">
            <template v-for="field in entityFields">
                <v-text-field v-if="field.type == 'datetime-local'" :rules="[field.required && required]"
                    v-model="entityData[field.value]" :label="field.name" :type="field.type">
                </v-text-field>

                <v-text-field v-else-if="field.type == 'date'" :rules="[field.required && required]"
                    :value="formatDate(entityData[field.value])"
                    @input="newVal => entityData[field.value] = new Date(newVal).toISOString()" :label="field.name"
                    :type="field.type">
                </v-text-field>

                <v-autocomplete v-else-if="field.type == 'select' && isType(field.items, 'string')"
                    :disabled="field.deps && field.deps.some(dep => !entityData[dep.slice(0, -1)])"
                    v-model="entityData[field.value]" :multiple="field.multiple" :rules="[!field.required || required]"
                    :label="$t(field.multiple ? field.items : field.items.slice(0, -1))"
                    :items="field.filter ? field.itemsData.filter(item => item[field.filter] == entityData[field.filter]) : field.itemsData"
                    :item-text="field.itemText" :item-value="field.itemValue" :loading="crudController.loading[field.items]" @change="onSelectChange(field)" />

                <v-autocomplete v-else-if="field.type == 'select' && isArray(field.items)" v-model="entityData[field.value]"
                    :multiple="field.multiple" :rules="[!field.required || required]" :label="field.name"
                    :items="field.items" />

                <CrudTable :parent="isParent(field)" child v-else-if="field.type == 'table'" :entity="field.value"
                    :model="models[`${field.value.slice(0, -1)}Model`]" :filter="filteredEndpoint"
                    :currentAction="entityAction" />

                <v-text-field v-else-if="field.type == 'time'" :rules="[field.required && required]"
                    :value="isoToTime(entityData[field.value])"
                    @input="newVal => entityData[field.value] = timeToIso(newVal)" :label="field.name" :type="field.type" />

                <template v-else-if="field.type == '3statebool'">
                    <label>{{ field.name }}</label>
                    <v-radio-group v-model="entityData[field.value]">
                        <v-radio :label="$t('undefinedValue')" :value="undefined"></v-radio>
                        <v-radio :label="$t('trueValue')" :value="true"></v-radio>
                        <v-radio :label="$t('falseValue')" :value="false"></v-radio>
                    </v-radio-group>
                </template>

                <v-text-field v-else :rules="getRules(field)"
                    :value="entityData[field.value]" @input="newVal => entityData[field.value] = newVal" :label="field.name"
                    :type="field.type" />

            </template>
        </Dialog>
        <notifications position="bottom right" group="crudTable" />
    </div>
</template>

<script>
import utilsMixin from '@/mixins/utilsMixin.js';
import crudController from '@/js/crudController.js';
import Dialog from '@/components/common/Dialog';
import Row from '@/components/common/Row';
import Card from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import models from '@/js/formModels.js';

export default {
    name: "CrudTable",
    components: { Dialog, Row, Card, DataTable },
    mixins: [utilsMixin],
    props: {
        entity: String,
        model: Object,
        expandable: Boolean,
        filter: String,
        parent: Boolean,
        child: Boolean,
        currentAction: String
    },
    data() {
        return {
            models,
            crudController,
            // entity
            entityAction: 'add',
            showEntityDialog: false,
            entityFields: [],
            selectedEntitys: [],
            entityData: {},
            filteredEndpoint: "",
            entitySearchQuery: ""
        }
    },
    watch: {
        endpoint() {
            if (this.child && this.currentAction == 'modify') {
                this.crudController.getItems(this.endpoint);
            }
        },
        currentAction(newVal) {
            if (this.child && newVal == 'add') {
                this.crudController.data[this.entity] = []
            }
        },
        instant: true
    },
    created() {
        this.getEntityFields();
        this.setDefaultEntityData();
    },
    mounted() {
        if (!this.endpoint.split("/").includes("undefined")) {
            this.crudController.getItems(this.endpoint);
        }
    },
    computed: {
        endpoint() {
            if (this.child) {
                return `${this.filter}/${this.entity}`
            } else {
                return this.entity
            }
        },
    },
    methods: {
        getRules(field) {
            const rules = [];
            if (field.required) {
                rules.push(this.required)
            }
            if (field.type == 'email') {
                rules.push(this.isValidEmail)
            }
            return rules;
        },
        onSelectChange(currentField) {
            const fieldsWithDeps = {};
            this.entityFields.map(field => {
                if (field.deps) {
                    fieldsWithDeps[field.value] = field.deps;
                }
            })

            Object.entries(fieldsWithDeps).forEach(([fieldValue, fieldDeps]) => {
                if (fieldDeps.includes(currentField.items)) {
                    const dependantField = this.entityFields.find(field => field.value == fieldValue);

                    dependantField.itemsData = [];
                    this.entityData[fieldValue] = null;

                    if (fieldDeps.every(dep => !!this.entityData[dep.slice(0, -1)])) {
                        const endpoint = dependantField.deps.reduce((endpoint, currDep) => endpoint + `${currDep}/${this.entityData[currDep.slice(0, -1)]}/`, "")

                        this.crudController.getItems(endpoint + dependantField.items).then((response) => {
                            dependantField.itemsData = response;
                        });
                    }
                }
            });
        },
        isParent(field) {
            return Object.entries(models[`${field.value.slice(0, -1)}Model`]).find(([key, value]) => value.type == 'table') && true
        },
        filterEndpoint(id) {
            if (this.child && this.parent) {
                this.filteredEndpoint = `${this.endpoint}/${id}`;
            } else {
                this.filteredEndpoint = `${this.entity}/${id}`;
            }
        },
        timePicked(field) {
            const picker = this.$refs[`picker-${field}`][0];
            picker.selectingHour = true;
        },
        setDefaultEntityData() {
            this.entityFields.forEach(field => {
                switch (field.type) {
                    case "table":
                        break;
                    case "select":
                        if (this.showEntityDialog) {
                            if (!this.isArray(field.items)) {
                                // TODO: Handle select fields loading state
                                if (field.deps && field.deps.every(dep => !!this.entityData[dep.slice(0, -1)])) {
                                    let endpoint = field.deps.reduce((endpoint, currDep) => endpoint + `${currDep}/${this.entityData[currentFilter]}/`, "")
                                    this.crudController.getItems(endpoint + field.items).then((response) => {
                                        field.itemsData = response;
                                    });
                                } else if (!field.deps) {
                                    this.crudController.getItems(field.items).then((response) => {
                                        field.itemsData = response;
                                    });
                                }
                                this.$set(this.entityData, field.value, null);
                            }
                        }
                        break;
                    default:
                        if (this.entityAction == 'add') {
                            this.$set(this.entityData, field.value, null);
                        }
                        break;
                }
            })
        },
        getEntityFields() {
            this.entityFields = this.$t(`tableHeaders.${this.entity}`).map(field => {
                if (this.model[field.value] && field.value !== 'actions' && field.value !== 'id') {
                    let fieldData = {};
                    this.$set(fieldData, 'name', field.text);
                    this.$set(fieldData, 'value', field.value);
                    this.$set(fieldData, 'type', this.model[field.value].type);
                    this.$set(fieldData, 'multiple', this.model[field.value].multiple);
                    this.$set(fieldData, 'required', this.model[field.value].required);
                    this.$set(fieldData, 'items', this.model[field.value].items);
                    this.$set(fieldData, 'itemText', this.model[field.value].itemText);
                    this.$set(fieldData, 'itemValue', this.model[field.value].itemValue);
                    this.$set(fieldData, 'deps', this.model[field.value].deps);
                    this.$set(fieldData, 'itemsData', [])

                    return fieldData;
                }
            }).filter(field => field !== undefined);
        },
        // -------------------
        addEntity() {
            this.entityAction = 'add';
            this.showEntityDialog = true;
            this.setDefaultEntityData();
        },
        modifyEntity(entity) {
            this.entityAction = 'modify';
            this.showEntityDialog = true;
            this.setDefaultEntityData();
            this.$set(this, "entityData", JSON.parse(JSON.stringify(entity)));
        },
        cancelEntityAction() {
            this.showEntityDialog = false;
        },
        saveEntityAction() {
            switch (this.entityAction) {
                case 'add':
                    this.crudController.createItem(this.endpoint, this.entityData)
                    this.showEntityDialog = false;
                    break;

                case 'modify':
                    this.crudController.modifyItem(this.endpoint, this.entityData);
                    this.showEntityDialog = false;
                    break;

                default:
                    break;
            }
        }
    }
}
</script>